// WP-C1.1 (INT-1/2/4, SB-4): calendar-connection queries/mutations.
// WP-C2.1/C2.2 (INT-3): Track C2's OAuth start + revoke mutations added alongside.
// WP-C2.3 (CW-1..3, SB-1): event preview/create/update/delete + scope-upgrade mutations, against
// the foundation agent's routes/calendarEvents.ts — every one of these invalidates the agenda
// query on success (lib/agenda.ts's useInvalidateAgenda) so the Agenda page reflects the write
// immediately, matching every other mutation hook in this file's own CALENDARS_KEY pattern.
import {
  calendarConnectionListResponseSchema,
  calendarConnectionSchema,
  calendarEventDeleteResponseSchema,
  calendarEventPreviewResponseSchema,
  calendarEventSchema,
  calendarOAuthStartResponseSchema,
  type CalendarConnectionCreateRequest,
  type CalendarConnectionEditRequest,
  type CalendarConnectionResponse,
  type CalendarEventDeleteResponse,
  type CalendarEventPreviewResponse,
  type CalendarEventResponse,
  type CalendarEventUpdateRequest,
  type CalendarEventWriteInput,
  type CalendarOAuthProvider,
  type CalendarOAuthStartResponse,
} from '@pfos/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useInvalidateAgenda } from './agenda';
import { api } from './api';

export const CALENDARS_KEY = ['calendars'] as const;

export function useCalendars(): UseQueryResult<CalendarConnectionResponse[]> {
  return useQuery({
    queryKey: CALENDARS_KEY,
    queryFn: async () =>
      calendarConnectionListResponseSchema.parse(await api.get('/api/v1/calendars')).connections,
  });
}

function useInvalidateCalendars(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: CALENDARS_KEY });
  };
}

export function useCreateCalendar(): UseMutationResult<
  CalendarConnectionResponse,
  Error,
  CalendarConnectionCreateRequest
> {
  const invalidate = useInvalidateCalendars();
  return useMutation({
    mutationFn: async (input) =>
      calendarConnectionSchema.parse(await api.post('/api/v1/calendars', input)),
    onSuccess: invalidate,
  });
}

export function useEditCalendar(
  id: string,
): UseMutationResult<CalendarConnectionResponse, Error, CalendarConnectionEditRequest> {
  const invalidate = useInvalidateCalendars();
  return useMutation({
    mutationFn: async (input) =>
      calendarConnectionSchema.parse(await api.patch(`/api/v1/calendars/${id}`, input)),
    onSuccess: invalidate,
  });
}

export function useDeleteCalendar(id: string): UseMutationResult<void, Error, void> {
  const invalidate = useInvalidateCalendars();
  return useMutation({
    mutationFn: async () => {
      await api.delete(`/api/v1/calendars/${id}`);
    },
    onSuccess: invalidate,
  });
}

export function useRefreshCalendar(
  id: string,
): UseMutationResult<CalendarConnectionResponse, Error, void> {
  const invalidate = useInvalidateCalendars();
  return useMutation({
    mutationFn: async () =>
      calendarConnectionSchema.parse(await api.post(`/api/v1/calendars/${id}/refresh`)),
    onSuccess: invalidate,
  });
}

/** WP-C2.1/C2.2 (INT-3): kicks off Track C2's consent flow — the caller does
 * `window.location.assign(data.authorizationUrl)` on success (a real top-level navigation to
 * the provider, not something react-query can drive itself). */
export function useStartCalendarOAuth(): UseMutationResult<
  CalendarOAuthStartResponse,
  Error,
  CalendarOAuthProvider
> {
  return useMutation({
    mutationFn: async (provider) =>
      calendarOAuthStartResponseSchema.parse(
        await api.get(`/api/v1/calendars/oauth/${provider}/start`),
      ),
  });
}

/** WP-C2.1/C2.2 (SB-4): "revocable in Settings" — best-effort provider-side revoke, then the
 * local row (and its token vault) is gone regardless (see oauthConnectionService.ts's revoke()). */
export function useRevokeCalendar(id: string): UseMutationResult<void, Error, void> {
  const invalidate = useInvalidateCalendars();
  return useMutation({
    mutationFn: async () => {
      await api.post(`/api/v1/calendars/${id}/revoke`);
    },
    onSuccess: invalidate,
  });
}

/** WP-C2.3 (SB-1): re-consent for a connection stuck on WP-C2.1/C2.2's original read-only
 * grant. Same "the caller does `window.location.assign`" contract as useStartCalendarOAuth —
 * this never touches CALENDARS_KEY itself (the row's capability isn't reflected in any response
 * field either before or after; only the provider-side grant changes). */
export function useUpgradeCalendarOAuth(
  id: string,
): UseMutationResult<CalendarOAuthStartResponse, Error, void> {
  return useMutation({
    mutationFn: async () =>
      calendarOAuthStartResponseSchema.parse(
        await api.post(`/api/v1/calendars/${id}/oauth/upgrade`),
      ),
  });
}

// --- WP-C2.3 (CW-1..3, SB-1): calendar EVENT write, against a specific connection's events ----

/** CW-1: "conflicts are warned before confirm" — a pure read against the connection's local
 * cache (never blocks submission itself; CalendarEventForm decides how to show the result). */
export function usePreviewCalendarEvent(
  connectionId: string,
): UseMutationResult<CalendarEventPreviewResponse, Error, CalendarEventWriteInput> {
  return useMutation({
    mutationFn: async (input) =>
      calendarEventPreviewResponseSchema.parse(
        await api.post(`/api/v1/calendars/${connectionId}/events/preview`, input),
      ),
  });
}

export function useCreateCalendarEvent(
  connectionId: string,
): UseMutationResult<CalendarEventResponse, Error, CalendarEventWriteInput> {
  const invalidateAgenda = useInvalidateAgenda();
  return useMutation({
    mutationFn: async (input) =>
      calendarEventSchema.parse(await api.post(`/api/v1/calendars/${connectionId}/events`, input)),
    onSuccess: invalidateAgenda,
  });
}

/** CW-2: edit/move — a partial patch; only fields the form actually changed need be present. */
export function useUpdateCalendarEvent(
  connectionId: string,
  uid: string,
): UseMutationResult<CalendarEventResponse, Error, CalendarEventUpdateRequest> {
  const invalidateAgenda = useInvalidateAgenda();
  return useMutation({
    mutationFn: async (input) =>
      calendarEventSchema.parse(
        await api.patch(`/api/v1/calendars/${connectionId}/events/${uid}`, input),
      ),
    onSuccess: invalidateAgenda,
  });
}

/** CW-2/SB-1: "delete requires double confirmation" — the caller (CalendarEventForm, via
 * ConfirmDialog's requireTypedConfirmation) has already made the user type the event's own
 * title back; this just forwards it as `confirmTitle` for the server's own re-check. A mismatch
 * surfaces as an ApiError (409 CONFIRMATION_MISMATCH) through the normal mutation onError path —
 * see api.ts's request(). */
export function useDeleteCalendarEvent(
  connectionId: string,
  uid: string,
): UseMutationResult<CalendarEventDeleteResponse, Error, string> {
  const invalidateAgenda = useInvalidateAgenda();
  return useMutation({
    mutationFn: async (confirmTitle) =>
      calendarEventDeleteResponseSchema.parse(
        await api.delete(`/api/v1/calendars/${connectionId}/events/${uid}`, { confirmTitle }),
      ),
    onSuccess: invalidateAgenda,
  });
}
